import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Fetch_productDetails } from "../../../hooks/APIHooks";
import { FaStar, FaShoppingCart, FaCheckCircle } from "react-icons/fa";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import NewArrivals from "./NewArrivals";
import { FaShippingFast, FaLock, FaBoxes } from "react-icons/fa";
import { FaRegRegistered } from "react-icons/fa";
import { SETTINGS_CRUD_API } from "../../../hooks/APIHooks";

const ProductDetails = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentImage, setCurrentImage] = useState("");
  const [variants, setVariants] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [productPrice, setProductPrice] = useState(null);
  const [variantFeedback, setVariantFeedback] = useState("");
  const [isOutOfStock, setIsOutOfStock] = useState(false);
  const [companyData, setCompanyData] = useState();
  const [imageErrors, setImageErrors] = useState({});

  useEffect(() => {
    fetchCompanySettings();
  }, []);

  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(SETTINGS_CRUD_API);
      if (response.data && !response.data.error) {
        setCompanyData(response.data);
      }
    } catch (err) {
      console.error("Error fetching company settings:", err);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) {
      return "";
    }

    if (imagePath.startsWith("http")) return imagePath;

    const cleanPath = imagePath.replace(/^(\.?\/?)?/, "");

    // Handle different environments
    let baseUrl;
    const { hostname, port, protocol } = window.location;

    if (hostname === "localhost" && port === "3000") {
      // React dev server on port 3000, APIs on port 80
      baseUrl = `${protocol}//${hostname}`;
    } else if (hostname === "localhost" && !port) {
      // React build served from same domain as APIs
      baseUrl = window.location.origin;
    } else {
      // Production environment
      baseUrl = window.location.origin;
    }

    return `${baseUrl}/apis/${cleanPath}`;
  };

  const handleImageError = (imageIndex) => {
    setImageErrors((prev) => ({
      ...prev,
      [imageIndex]: true,
    }));
  };

  const handleImageLoad = (imageIndex) => {
    setImageErrors((prev) => ({
      ...prev,
      [imageIndex]: false,
    }));
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    // Add full stars
    for (let i = 0; i < fullStars; i++) {
      stars.push(<FaStar key={`full-${i}`} className="text-orange-500" />);
    }

    // Add half star if applicable
    if (hasHalfStar) {
      stars.push(<FaStar key="half" className="text-orange-500 opacity-50" />);
    }

    // Add empty stars to make a total of 5
    for (let i = fullStars + (hasHalfStar ? 1 : 0); i < 5; i++) {
      stars.push(<FaStar key={`empty-${i}`} className="text-gray-300" />);
    }

    return stars;
  };

  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        const response = await axios.get(
          `${Fetch_productDetails}?product_id=${productId}`
        );
        if (response.data.success) {
          setProduct(response.data.data.product);
          setVariants(response.data.data.variants || []);

          // Get all product images and construct proper URLs
          const images = [
            response.data.data.product.product_image,
            response.data.data.product.product_image_1,
            response.data.data.product.product_image_2,
            response.data.data.product.product_image_3,
            response.data.data.product.product_image_4,
            response.data.data.product.product_image_5,
          ]
            .filter(Boolean)
            .map((img) => getImageUrl(img));

          setCurrentImage(images[0] || "");
          setProductPrice(response.data.data.product.price);

          // Check stock availability for the main product
          setIsOutOfStock(response.data.data.product.stock_quantity === 0);

          // Handle variants
          if (
            response.data.data.variants &&
            response.data.data.variants.length > 0
          ) {
            const firstVariant = response.data.data.variants[0];
            setSelectedVariant(firstVariant);
            setProductPrice(
              firstVariant.product_variant_mapping.variant_prices
            );
            setVariantFeedback(
              `Selected variant: ${firstVariant.variant_option.option_value}`
            );

            // Check if the selected variant is out of stock
            setIsOutOfStock(
              firstVariant.product_variant_mapping.variant_stock_quantity === 0
            );
          } else {
            // No variants available, set default selected variant
            setSelectedVariant({
              product_variant: {
                variant_name: response.data.data.product.product_name,
              },
              variant_option: { option_value: "Default" },
              product_variant_mapping: { variant_prices: 0 },
            });
          }
        } else {
          setError(response.data.error);
        }
      } catch (err) {
        setError("Error fetching product details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [productId]);

  if (error) {
    return <div className="text-red-500 py-8 text-center">Error: {error}</div>;
  }

  if (!product) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const groupVariantsByName = (variants) => {
    return variants.reduce((acc, variant) => {
      const name = variant.product_variant.variant_name;
      if (!acc[name]) {
        acc[name] = [];
      }
      acc[name].push(variant);
      return acc;
    }, {});
  };

  const handleVariantSelection = (variant) => {
    setSelectedVariant(variant);
    setProductPrice(variant.product_variant_mapping.variant_prices);
    setVariantFeedback(
      `Selected variant: ${variant.variant_option.option_value}`
    );

    // Check if the selected variant is out of stock
    setIsOutOfStock(
      variant.product_variant_mapping.variant_stock_quantity === 0
    );
  };

  const handleAddToCart = () => {
    if (!selectedVariant) return;

    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    const existingProduct = cart.find(
      (item) =>
        item.product_id === product.product_id &&
        item.variant_id === selectedVariant?.variant_id
    );

    let updatedCart;
    let priceToUse;

    // Check product type and determine the price to use
    if (product.product_type === "single") {
      // For single product type
      priceToUse = product.sale_price > 0 ? product.sale_price : productPrice;
    } else if (product.product_type === "multivariate") {
      // For multivariate product type
      priceToUse =
        selectedVariant.product_variant_mapping.variant_sale_prices > 0
          ? selectedVariant.product_variant_mapping.variant_sale_prices
          : selectedVariant.product_variant_mapping.variant_prices;
    }

    if (existingProduct) {
      updatedCart = cart.map((item) =>
        item.product_id === product.product_id &&
        item.variant_id === selectedVariant?.variant_id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
    } else {
      updatedCart = [
        ...cart,
        {
          ...product,
          quantity: 1,
          price: priceToUse,
          variant: selectedVariant
            ? `${product.product_name} - ${selectedVariant.variant_option.option_value}`
            : "",
          variant_id: selectedVariant?.variant_id || product.product_id,
        },
      ];
    }

    localStorage.setItem("cart", JSON.stringify(updatedCart));
    navigate("/Store/cart");
  };

  const handleBuyNow = () => {
    if (!selectedVariant) return;

    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    let priceToUse;

    // Check product type and determine the price to use
    if (product.product_type === "single") {
      priceToUse = product.sale_price > 0 ? product.sale_price : productPrice;
    } else if (product.product_type === "multivariate") {
      priceToUse =
        selectedVariant.product_variant_mapping.variant_sale_prices > 0
          ? selectedVariant.product_variant_mapping.variant_sale_prices
          : selectedVariant.product_variant_mapping.variant_prices;
    }

    const updatedCart = [
      ...cart.filter((item) => item.product_id !== product.product_id),
      {
        ...product,
        quantity: 1,
        price: priceToUse,
        variant: selectedVariant
          ? `${product.product_name} - ${selectedVariant.variant_option.option_value}`
          : "",
        variant_id: selectedVariant?.variant_id || product.product_id,
      },
    ];

    localStorage.setItem("cart", JSON.stringify(updatedCart));
    navigate("/Store/checkout");
  };

  // Get all product images with proper URLs
  const images = [
    product.product_image,
    product.product_image_1,
    product.product_image_2,
    product.product_image_3,
    product.product_image_4,
    product.product_image_5,
  ]
    .filter(Boolean)
    .map((img) => getImageUrl(img));

  const getFallbackContent = () => {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-200 rounded-lg">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-600 mb-2">
            {product.product_name
              ? product.product_name.charAt(0).toUpperCase()
              : "?"}
          </div>
          <div className="text-xs text-gray-500 px-2">
            {product.product_name || "No Image"}
          </div>
        </div>
      </div>
    );
  };

  return (
    <section>
   

      <section className="mx-5">
        <div className="container mx-auto my-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              {/* Main Image */}
              <div className="w-full h-auto max-w-md mx-auto rounded-lg shadow-md overflow-hidden bg-gray-100">
                {currentImage && !imageErrors["main"] ? (
                  <img
                    src={currentImage}
                    alt={product.product_name}
                    className="w-full h-auto object-cover transition-transform transform hover:scale-105"
                    onError={() => handleImageError("main")}
                    onLoad={() => handleImageLoad("main")}
                  />
                ) : (
                  getFallbackContent()
                )}
              </div>

              {/* Thumbnail Images */}
              <div className="flex gap-4 mt-4 overflow-x-auto">
                {images.map((image, index) => (
                  <div
                    key={index}
                    className={`w-20 h-20 flex-shrink-0 rounded-lg cursor-pointer transition-transform transform hover:scale-105 overflow-hidden bg-gray-100 ${
                      currentImage === image ? "ring-2 ring-primary" : ""
                    }`}
                    onClick={() => setCurrentImage(image)}
                  >
                    {!imageErrors[index] ? (
                      <img
                        src={image}
                        alt={`${product.product_name} - ${index}`}
                        className="w-full h-full object-cover"
                        onError={() => handleImageError(index)}
                        onLoad={() => handleImageLoad(index)}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-200">
                        <span className="text-gray-500 text-xs">
                          {index + 1}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Fixed Bottom Buttons */}
              <div className="fixed bottom-0 left-0 right-0 shadow-lg z-10">
                <div className="flex">
                  <button
                    className={`flex items-center justify-center text-white font-bold text-center uppercase transition-all text-[12px] py-5 w-full ${
                      isOutOfStock
                        ? "bg-gray-400 cursor-not-allowed"
                        : selectedVariant
                        ? "bg-black hover:bg-gray-800"
                        : "bg-gray-400 cursor-not-allowed"
                    }`}
                    onClick={handleAddToCart}
                    disabled={isOutOfStock || !selectedVariant}
                  >
                    <FaShoppingCart className="mr-2" /> Add to Cart
                  </button>
                  <button
                    className={`flex items-center justify-center text-white font-bold text-center uppercase transition-all text-[12px] py-5 w-full ${
                      isOutOfStock
                        ? "bg-gray-400 cursor-not-allowed"
                        : selectedVariant
                        ? "bg-primary hover:bg-yellow-700"
                        : "bg-gray-400 cursor-not-allowed"
                    }`}
                    onClick={handleBuyNow}
                    disabled={isOutOfStock || !selectedVariant}
                  >
                    <FaCheckCircle className="mr-2" /> Buy Now
                  </button>
                </div>
              </div>
            </div>

            <div>
              {/* Product Info */}
              <div className="border rounded-md p-4">
                <div className="leading-tight">
                  <h1 className="text-2xl md:text-3xl font-bold ">
                    {product.product_type === "multivariate" && selectedVariant
                      ? `${product.product_name} - ${selectedVariant.variant_option.option_value}`
                      : product.product_name}
                  </h1>
                  <span className="text-[14px] text-gray-500">
                    Partnered and Distributed by
                  </span>
                  <p className="font-medium">
                    {companyData?.companyName || "Company"}
                  </p>
                  <p className="text-[12px]">SKU - {product.sku}</p>
                </div>

                <p>
                  Stock:{" "}
                  {product.product_type === "multivariate" && selectedVariant
                    ? selectedVariant.product_variant_mapping
                        .variant_stock_quantity
                    : product.stock_quantity}
                </p>

                <p className="text-mg flex items-center justify-center rounded-full w-fit text-white py-1 space-x-1">
                  {renderStars(product.product_rating)}{" "}
                  <span className="text-black">({product.product_rating})</span>
                </p>

                <p className="text-[12px] leading-tight tracking-tighter bg-black mb-4 w-fit p-2 flex font-medium rounded-r-full text-center text-white">
                  <FaRegRegistered size={20} className="mr-1 text-primary" />
                  <span>
                    Assured by{" "}
                    <span className="text-primary text-[14px]">
                      thewinnerindia
                    </span>
                  </span>
                </p>

                {/* Price */}
                {product.product_type === "single" ? (
                  <div className="flex flex-col gap-2">
                    {product.sale_price &&
                      product.sale_price < productPrice && (
                        <div>
                          <p className="text-[12px] font-semibold text-gray-500 leading-tight">
                            M.R.P
                          </p>

                          {product.sale_price > 0 ? (
                            <p className="text-[18px] md:text-2xl font-semibold text-black line-through">
                              ₹ {productPrice}
                            </p>
                          ) : (
                            <p className="text-[18px] md:text-2xl font-semibold text-black">
                              ₹ {productPrice}
                            </p>
                          )}
                        </div>
                      )}
                    {product.sale_price > 0 && (
                      <div>
                        <p className="text-[14px] bg-green-600 px-2 max-w-fit rounded-r-full font-semibold text-white leading-tight">
                          Sale price
                        </p>
                        <p className="text-[18px] md:text-2xl font-semibold text-black">
                          ₹ {product.sale_price}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {selectedVariant && (
                      <>
                        {selectedVariant.product_variant_mapping
                          .variant_sale_prices > 0 && (
                          <div>
                            <p className="text-[12px] font-semibold text-gray-500 leading-tight">
                              M.R.P
                            </p>
                            <p className="text-[18px] md:text-2xl font-semibold text-black line-through">
                              ₹{" "}
                              {
                                selectedVariant.product_variant_mapping
                                  .variant_prices
                              }
                            </p>
                          </div>
                        )}
                        {selectedVariant.product_variant_mapping
                          .variant_sale_prices > 0 ? (
                          <div>
                            <p className="text-[14px] bg-green-600 px-2 max-w-fit rounded-r-full font-semibold text-white leading-tight">
                              Sale price
                            </p>
                            <p className="text-[18px] md:text-2xl font-semibold text-black">
                              ₹{" "}
                              {
                                selectedVariant.product_variant_mapping
                                  .variant_sale_prices
                              }
                            </p>
                          </div>
                        ) : (
                          <p className="text-[18px] md:text-2xl font-semibold text-black">
                            ₹{" "}
                            {
                              selectedVariant.product_variant_mapping
                                .variant_prices
                            }
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}

                <div className="text-[14px]">Inclusive of all taxes</div>

                {isOutOfStock && (
                  <p className="text-red-500 font-semibold mt-2">
                    Out of Stock
                  </p>
                )}
              </div>

              {/* Variants Section */}
              {product.product_type === "multivariate" && (
                <section>
                  {variantFeedback && (
                    <div className="mt-2 text-green-600 text-center font-semibold">
                      {variantFeedback}
                    </div>
                  )}
                  <div>
                    <h2 className="text-[18px]  font-semibold">
                      Available Variants
                    </h2>
                    {variants && variants.length > 0 ? (
                      <div className="mt-4 space-y-6">
                        {Object.keys(groupVariantsByName(variants)).map(
                          (variantName, index) => (
                            <div
                              key={index}
                              className="p-4 border rounded-lg shadow-md bg-white"
                            >
                              <h3 className="text-[14px]  font-bold mb-2">
                                {variantName}
                              </h3>

                              <div className="flex flex-wrap gap-2">
                                {groupVariantsByName(variants)[variantName].map(
                                  (variant, idx) => (
                                    <div
                                      key={idx}
                                      className="p-4 border border-black rounded-full shadow-md bg-gray-50 cursor-pointer hover:bg-primary transition"
                                      onClick={() =>
                                        handleVariantSelection(variant)
                                      }
                                    >
                                      <p className="text-[14px] font-bold text-center leading-tight w-auto h-auto flex items-center justify-center">
                                        {variant.variant_option.option_value}
                                      </p>
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    ) : (
                      <p>No variants available for this product.</p>
                    )}
                  </div>
                </section>
              )}

              {/* Product Description and Details */}
              <div className="border rounded-md p-4 mt-2">
                <h2 className="text-[22px] font-semibold">About this item</h2>
                <p className="text-[14px] font-medium rounded-md text-gray-600 mt-2">
                  {product.product_description}
                </p>

                <table className="min-w-full mt-2 border-collapse shadow-md rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-gray-300 text-gray-800">
                      <th className="border px-4 py-2 text-left font-semibold">
                        Dimension
                      </th>
                      <th className="border px-4 py-2 text-left font-semibold">
                        Value
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-white hover:bg-gray-100">
                      <td className="border px-4 py-2">Weight</td>
                      <td className="border px-4 py-2">
                        {product.weight_kg} kg
                      </td>
                    </tr>
                    <tr className="bg-white hover:bg-gray-100">
                      <td className="border px-4 py-2">Length</td>
                      <td className="border px-4 py-2">
                        {product.length_cm} cm
                      </td>
                    </tr>
                    <tr className="bg-white hover:bg-gray-100">
                      <td className="border px-4 py-2">Width</td>
                      <td className="border px-4 py-2">
                        {product.width_cm} cm
                      </td>
                    </tr>
                    <tr className="bg-white hover:bg-gray-100">
                      <td className="border px-4 py-2">Height</td>
                      <td className="border px-4 py-2">
                        {product.height_cm} cm
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Company Ensure Section */}
              <section className="my-5">
                <div className="grid grid-cols-3 text-center">
                  <div className="flex items-center space-x-2 mx-auto">
                    <FaLock className="text-[25px]" />
                    <div className="text-left leading-tight">
                      <p className="sm:text-[16px] text-[12px]">
                        Secure & safe
                      </p>
                      <p className="sm:text-[12px] text-[10px]">
                        Safe delivery
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 mx-auto">
                    <FaShippingFast className="text-[25px]" />
                    <div className="text-left leading-tight">
                      <p className="sm:text-[16px] text-[12px]">
                        Fast delivery
                      </p>
                      <p className="sm:text-[12px] text-[10px]">5000+ dtyles</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 mx-auto">
                    <FaBoxes className="text-[25px]" />
                    <div className="text-left leading-tight">
                      <p className="sm:text-[16px] text-[12px]">
                        Free shipping
                      </p>
                      <p className="sm:text-[12px] text-[10px]">
                        For orders 990+
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>

      <NewArrivals />
    </section>
  );
};

export default ProductDetails;
